/**
 * List of CSS selectors for focusable elements in the drawer.
 * @type {string[]}
 */
export const focusableSelectors = [
  "a[href]",
  "area[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  '[tabindex]:not([tabindex="-1"]):not([tabindex="-9999"])',
  "[contenteditable]",
]

/**
 * Returns all focusable elements inside the given drawer element.
 * @param {HTMLElement} drawer - The drawer DOM element.
 * @return {HTMLElement[]} Array of focusable elements.
 */
export function getFocusableEls(drawer) {
  return Array.from(
    drawer.querySelectorAll(focusableSelectors.join(","))
  ).filter(el => el.offsetParent !== null && !el.hasAttribute("disabled"))
}

/**
 * Handles tab key navigation to trap focus within the drawer.
 * @param {KeyboardEvent} e - The keyboard event.
 * @param {HTMLElement[]} focusableEls - Array of focusable elements.
 * @param {HTMLElement} drawer - The drawer DOM element.
 */
export function trapTabKey(e, focusableEls, drawer) {
  if (e.key !== "Tab") return
  if (focusableEls.length === 0) return
  if (focusableEls.length === 1) {
    e.preventDefault()
    focusableEls[0].focus()
    return
  }
  const activeIndex = focusableEls.indexOf(document.activeElement)
  if (e.shiftKey && activeIndex === 0) {
    e.preventDefault()
    focusableEls[focusableEls.length - 1].focus()
  } else if (!e.shiftKey && activeIndex === focusableEls.length - 1) {
    e.preventDefault()
    focusableEls[0].focus()
  } else if (!drawer.contains(document.activeElement)) {
    e.preventDefault()
    focusableEls[0].focus()
  }
}

/**
 * Sets aria-hidden="true" on all body children except the drawer and backdrop.
 * @param {HTMLElement} drawer - The drawer DOM element.
 * @param {string} backdropClass - The CSS class for the backdrop.
 * @return {HTMLElement[]} Array of elements that had aria-hidden set.
 */
export function setAriaHidden(drawer, backdropClass) {
  const bodyChildren = Array.from(document.body.children)
  const toHide = bodyChildren.filter(
    el => el !== drawer && !el.classList.contains(backdropClass)
  )
  toHide.forEach(el => el.setAttribute("aria-hidden", "true"))
  return toHide
}

/**
 * Removes aria-hidden attribute from the given elements.
 * @param {HTMLElement[]} toHide - Array of elements to remove aria-hidden from.
 */
export function removeAriaHidden(toHide) {
  toHide.forEach(el => el.removeAttribute("aria-hidden"))
}
